import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { useToast } from '@/hooks/use-toast';
import { useSubscription } from '@/hooks/useSubscription';
import { supabase } from '@/integrations/supabase/client';
import { 
  CreditCard, 
  Plus, 
  Download, 
  Calendar, 
  DollarSign, 
  Shield,
  CheckCircle,
  AlertCircle,
  Lock,
  Eye,
  Trash2,
  ExternalLink
} from 'lucide-react';

interface PaymentMethod {
  id: string;
  type: 'card';
  brand: string;
  last4: string;
  exp_month: number;
  exp_year: number;
  is_default: boolean;
}

interface Invoice {
  id: string;
  date: string;
  amount: number;
  currency: string;
  status: 'paid' | 'pending' | 'failed';
  description: string;
  pdf_url?: string;
}

export function PaymentSection() {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { subscription } = useSubscription();
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(false);
  const [subscriptionData, setSubscriptionData] = useState(null);
  
  const hasActiveSubscription = subscription?.subscribed;

  // Load actual payment data on component mount
  useEffect(() => {
    loadPaymentData();
  }, []);

  const loadPaymentData = async () => {
    setLoading(true);
    try {
      // In real implementation, this would fetch from Stripe via edge functions
      // For now, we show a secure message about data handling
      toast({
        title: "Loading Payment Data",
        description: "Payment data is securely processed through Stripe. No sensitive information is stored locally.",
      });
    } catch (error) {
      console.error('Error loading payment data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddPaymentMethod = async () => {
    setLoading(true);
    try {
      // Redirect to Stripe's secure hosted page for PCI compliance
      const response = await supabase.functions.invoke('create-checkout');
      if (response.data?.url) {
        window.open(response.data.url, '_blank');
      } else {
        throw new Error('Unable to create secure payment session');
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to open secure payment form. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleManageSubscription = async () => {
    if (!hasActiveSubscription) {
      toast({
        title: "No Active Subscription",
        description: "Subscribe first to access payment management.",
        variant: "destructive",
      });
      navigate('/pricing');
      return;
    }
    
    setLoading(true);
    try {
      const response = await supabase.functions.invoke('customer-portal');
      const errMsg: string | undefined =
        (response.data as any)?.error ||
        (response.error as any)?.context?.error ||
        (response.error as any)?.message;
      if (errMsg && /no stripe customer/i.test(errMsg)) {
        toast({
          title: "No billing account yet",
          description:
            "Your access was granted manually, so there's no Stripe billing to manage. Contact support if you need changes.",
        });
        return;
      }
      if (response.data?.url) {
        window.open(response.data.url, '_blank');
      } else {
        throw new Error('Unable to access payment management');
      }
    } catch (error) {
      toast({
        title: "Error", 
        description: "Unable to access secure payment management. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleExportData = async () => {
    if (!hasActiveSubscription) {
      toast({
        title: "No Active Subscription",
        description: "Subscribe first to access your data.",
        variant: "destructive",
      });
      navigate('/pricing');
      return;
    }
    
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('customer-portal', {
        body: { return_url: window.location.href }
      });

      if (error) throw error;

      if (data?.url) {
        window.open(data.url, '_blank');
      }
    } catch (error) {
      console.error('Error accessing portal:', error);
      toast({
        title: "Error",
        description: "Unable to access data export. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePrivacyPolicy = () => {
    // Open privacy policy in new tab
    window.open('https://odyvoy.lovable.app', '_blank');
  };

  const handleDeleteAllPaymentData = async () => {
    if (!hasActiveSubscription) {
      toast({
        title: "No Active Subscription",
        description: "You need an active subscription to manage payment data.",
        variant: "destructive",
      });
      navigate('/pricing');
      return;
    }
    
    if (!confirm('This will permanently delete all your payment data. This action cannot be undone. Continue?')) {
      return;
    }
    
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('customer-portal', {
        body: { return_url: window.location.href }
      });

      if (error) throw error;

      if (data?.url) {
        window.open(data.url, '_blank');
        toast({
          title: "Redirecting to Secure Portal",
          description: "You can request data deletion through the Stripe Customer Portal.",
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Unable to access secure portal. Please contact support.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSetDefault = async (methodId: string) => {
    setPaymentMethods(methods => 
      methods.map(method => ({
        ...method,
        is_default: method.id === methodId
      }))
    );
    toast({
      title: "Default payment method updated",
      description: "Your default payment method has been changed.",
    });
  };

  const handleRemovePaymentMethod = async (methodId: string) => {
    setPaymentMethods(methods => methods.filter(method => method.id !== methodId));
    toast({
      title: "Payment method removed",
      description: "The payment method has been removed from your account.",
    });
  };

  const handleDownloadInvoice = (invoice: Invoice) => {
    toast({
      title: "Downloading invoice",
      description: `Invoice ${invoice.id} will be downloaded.`,
    });
  };

  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(amount / 100);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getCardIcon = (brand: string) => {
    const icons = {
      visa: '💳',
      mastercard: '💳',
      amex: '💳',
      discover: '💳',
    };
    return icons[brand as keyof typeof icons] || '💳';
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'paid':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-muted text-foreground';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'paid':
        return <CheckCircle className="h-4 w-4" />;
      case 'pending':
        return <Calendar className="h-4 w-4" />;
      case 'failed':
        return <AlertCircle className="h-4 w-4" />;
      default:
        return null;
    }
  };

  return (
    <div className="space-y-8 max-w-4xl">
      {/* Header */}
      <div className="space-y-2">
        <h2 className="text-2xl font-semibold tracking-tight">Payments & Billing</h2>
        <p className="text-muted-foreground">
          Manage your payment methods and billing information securely
        </p>
      </div>

      {/* Security Notice */}
      <Card className="border-primary/20 bg-gradient-to-r from-primary/5 to-primary/10">
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            <div className="rounded-full bg-primary/10 p-3">
              <Shield className="h-6 w-6 text-primary" />
            </div>
            <div className="space-y-3 flex-1">
              <h3 className="text-lg font-semibold">Bank-Level Security</h3>
              <p className="text-muted-foreground leading-relaxed">
                Your payment data is processed by Stripe with PCI DSS Level 1 compliance. 
                We never store sensitive payment information on our servers.
              </p>
              <div className="flex flex-wrap gap-3 pt-2">
                <Button
                  variant="default"
                  onClick={handleManageSubscription}
                  disabled={loading}
                  className="h-9"
                >
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Secure Portal
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleDeleteAllPaymentData}
                  className="h-9 text-destructive border-destructive/30 hover:bg-destructive/5"
                  disabled={loading}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete Data
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Payment Methods */}
      <Card>
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="h-5 w-5 text-primary" />
                Payment Methods
              </CardTitle>
              <CardDescription>
                Manage your payment methods through Stripe's secure platform
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="text-center py-8 space-y-4">
            <div className="rounded-full bg-muted/30 w-16 h-16 mx-auto flex items-center justify-center">
              <Shield className="h-8 w-8 text-muted-foreground/60" />
            </div>
            <div className="space-y-2">
              <h3 className="font-medium">Secure Payment Management</h3>
              <p className="text-sm text-muted-foreground max-w-md mx-auto">
                For your security, payment methods are managed through Stripe's encrypted platform. 
                No sensitive data is stored locally.
              </p>
            </div>
          </div>

          <Button
            variant="outline"
            className="w-full h-12 bg-gradient-to-r from-background to-muted/20 border-primary/20 hover:bg-primary/5"
            onClick={handleManageSubscription}
            disabled={loading}
          >
            <ExternalLink className="h-4 w-4 mr-2" />
            Open Payment Manager
          </Button>
        </CardContent>
      </Card>

      {/* Subscription Status */}
      <Card>
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2">
            <DollarSign className="h-5 w-5 text-primary" />
            Subscription
          </CardTitle>
          <CardDescription>
            View and manage your subscription details
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="text-center py-8 space-y-4">
            <div className="rounded-full bg-primary/10 w-16 h-16 mx-auto flex items-center justify-center">
              <Shield className="h-8 w-8 text-primary" />
            </div>
            <div className="space-y-2">
              <h3 className="text-lg font-medium">Secure Access Required</h3>
              <p className="text-sm text-muted-foreground max-w-md mx-auto">
                Access your billing history, invoices, and subscription details through 
                Stripe's secure customer portal.
              </p>
            </div>
          </div>

          <Button
            onClick={handleManageSubscription}
            disabled={loading}
            className="w-full h-12"
          >
            <ExternalLink className="h-4 w-4 mr-2" />
            Access Customer Portal
          </Button>

          <div className="bg-amber-50/50 border border-amber-200/50 p-4 rounded-lg">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
              <div className="space-y-1">
                <h4 className="font-medium text-amber-800">Privacy Protection</h4>
                <p className="text-sm text-amber-700">
                  We follow GDPR guidelines by keeping sensitive billing data on Stripe's secure servers only.
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

    </div>
  );
}